import { fetchBaseUrl } from "core/utils/common";

const apiURL = `${fetchBaseUrl({ port: 8000 })}/tma`;

export interface UploadImageResponse {
  url?: string;
  download_url?: string;
  file_id?: string;
  file_key?: string;
  message?: string;
  [key: string]: any;
}

/**
 * Uploads an architecture diagram image to the threat model API.
 *
 * @param file              - Binary image file (png, jpeg, jpg, webp)
 * @param bearerToken       - X-API-KEY header value
 * @param imageDescription  - Optional description of the image (query param)
 * @param downloadExpiresIn - Optional expiry time in seconds (query param, default: 3600)
 */
export async function uploadArchitectureDiagram(
  file: File,
  bearerToken: string,
  imageDescription?: string,
  downloadExpiresIn?: number
): Promise<UploadImageResponse> {
  // image_description and download_expires_in are query parameters
  const params = new URLSearchParams();
  if (imageDescription) {
    params.append("image_description", imageDescription);
  }
  params.append("download_expires_in", String(downloadExpiresIn ?? 3600));

  const url = `${apiURL}/api/upload/upload-combined?${params.toString()}`;

  // Only file goes in the multipart/form-data body
  const formData = new FormData();
  formData.append("file", file);

  console.log("[uploadArchitectureDiagram] POST", url);
  console.log("[uploadArchitectureDiagram] file:", file.name, file.type, file.size);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "X-API-KEY": bearerToken,
      // NOTE: Do NOT set Content-Type — browser sets it with the multipart boundary
    },
    body: formData,
  });

  const responseText = await response.text();
  console.log("[uploadArchitectureDiagram] status:", response.status);
  console.log("[uploadArchitectureDiagram] response body:", responseText);

  if (!response.ok) {
    throw new Error(`Upload failed (${response.status}): ${responseText}`);
  }

  try {
    const data = JSON.parse(responseText);
    console.log("[uploadArchitectureDiagram] parsed response:", data);
    return data;
  } catch {
    throw new Error(`Upload succeeded but response is not JSON: ${responseText}`);
  }
}

export interface ThreatCoreResponse {
  [key: string]: any;
}

// ── Report Generate ────────────────────────────────────────────────────────────

export interface ReportAsset {
  name: string;
  type: string;
  description?: string;
  confidence?: number;
  category?: string | null;
  properties?: any | null;
}

export interface ReportFlow {
  flow_description: string;
  source_entity: string;
  target_entity: string;
  flow_type: string;
  criticality: string;
}

export interface ReportTrustBoundary {
  purpose: string;
  source_entity: string;
  target_entity: string;
  boundary_type: string;
  security_controls?: string[];
}

export interface ReportGenerateResponse {
  file_key?: string;
  summary_data?: {
    step?: string;
    analysis?: string;
    agent?: string;
    file_key?: string;
    error?: boolean;
  };
  assets_data?: ReportAsset[];
  flows_data?: ReportFlow[];
  trust_boundaries_data?: ReportTrustBoundary[];
  metadata?: Record<string, string>;
  [key: string]: any;
}

// ── Threat Sources Identify ────────────────────────────────────────────────────

export interface ThreatActor {
  actor_type: string;
  motivation?: string;
  capability?: string;
  description?: string;
}

export interface AttackVector {
  name: string;
  description?: string;
  targeted_asset?: string;
  severity?: string;
  stride_category?: string;
  prerequisites?: string[];
}

export interface ThreatSourcesResponse {
  file_key?: string;
  success?: boolean;
  results?: {
    threat_sources?: {
      threat_actors?: ThreatActor[];
      attack_vectors?: AttackVector[];
      total_threats?: number;
      threat_confidence?: number;
      critical_threats?: AttackVector[];
      processing_notes?: string;
    };
  };
  steps_completed?: string[];
  analysis_type?: string;
  [key: string]: any;
}

/**
 * Identifies threat sources for an uploaded architecture diagram.
 *
 * @param fileKey         - The file key returned by the upload API
 * @param bearerToken     - X-API-KEY header value
 * @param assets          - Asset extraction results from /core/stream
 * @param dataFlows       - Data flow results from /core/stream
 * @param trustBoundaries - Trust boundary results from /core/stream
 */
export async function identifyThreatSources(
  fileKey: string,
  bearerToken: string,
  assets?: any,
  dataFlows?: any,
  trustBoundaries?: any
): Promise<ThreatSourcesResponse> {
  const url = `${apiURL}/api/threat-sources/identify`;
  const body: Record<string, any> = { file_key: fileKey };
  if (assets) body.assets = {assets};
  if (dataFlows) body.data_flows = {dataFlows};
  if (trustBoundaries) body.trust_boundaries = {trustBoundaries};

  console.log("[identifyThreatSources] POST", url);
  console.log("[identifyThreatSources] request body:", JSON.stringify(body));

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": bearerToken,
    },
    body: JSON.stringify(body),
  });

  const responseText = await response.text();
  console.log("[identifyThreatSources] status:", response.status);
  console.log("[identifyThreatSources] response body:", responseText);

  if (!response.ok) {
    throw new Error(`Threat sources API failed (${response.status}): ${responseText}`);
  }

  try {
    return JSON.parse(responseText);
  } catch {
    throw new Error(`Threat sources succeeded but response is not JSON: ${responseText}`);
  }
}


/**
 * Generates a threat model report for an uploaded architecture diagram.
 *
 * @param fileKey     - The file key returned by the upload API
 * @param bearerToken - X-API-KEY header value
 */
export async function generateReport(
  fileKey: string,
  bearerToken: string
): Promise<ReportGenerateResponse> {
  const url = `${apiURL}/api/report/generate`;
  const body = {
    file_key: fileKey,
    format: "json",
    include_image: true,
    include_summary: true,
  };

  console.log("[generateReport] POST", url);
  console.log("[generateReport] request body:", JSON.stringify(body));

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": bearerToken,
    },
    body: JSON.stringify(body),
  });

  const responseText = await response.text();
  console.log("[generateReport] status:", response.status);
  console.log("[generateReport] response body:", responseText);

  if (!response.ok) {
    throw new Error(`Report generate API failed (${response.status}): ${responseText}`);
  }

  try {
    return JSON.parse(responseText);
  } catch {
    throw new Error(`Report generate succeeded but response is not JSON: ${responseText}`);
  }
}

/**
 * Streams the threat core analysis from /core/stream.
 * Emits a callback for each completed step event and a final callback when the stream ends.
 *
 * @param fileKey     - The file key returned by the upload API
 * @param bearerToken - X-API-KEY header value
 * @param assumptions - Optional list of assumption strings
 * @param onStep      - Called for each completed step with the step id and accumulated response
 * @param onComplete  - Called when the stream finishes with the final accumulated response
 * @param onError     - Called if the request or stream parsing fails
 * @param signal      - Optional AbortSignal to cancel the request
 */
export async function streamThreatCore(
  fileKey: string,
  bearerToken: string,
  assumptions: string[],
  onStep: (stepId: string, accumulated: ThreatCoreResponse) => void,
  onComplete: (finalResponse: ThreatCoreResponse) => void,
  onError: (err: Error) => void,
  signal?: AbortSignal
): Promise<void> {
  const requestBody: Record<string, any> = { file_key: fileKey };
  if (assumptions?.length > 0) {
    requestBody["Assumptions"] = assumptions;
  }

  // Maps progress message substrings to canonical step IDs
  const MESSAGE_TO_STEP: Record<string, string> = {
    "Asset extraction": "asset_extraction",
    "Data flow": "data_flow",
    "Trust boundary": "trust_boundary",
    "Threat source": "threat_source",
  };

  function stepFromMessage(message: string): string | null {
    for (const [key, id] of Object.entries(MESSAGE_TO_STEP)) {
      if (message.includes(key)) return id;
    }
    return null;
  }

  let response: Response;
  try {
    response = await fetch(`${apiURL}/api/threat/core/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-KEY": bearerToken,
      },
      body: JSON.stringify(requestBody),
      signal,
    });
  } catch (err: any) {
    onError(err);
    return;
  }

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => "");
    onError(new Error(`Stream API failed (${response.status}): ${text}`));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let accumulated: ThreatCoreResponse = { steps_completed: [] };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // Strip SSE "data: " prefix
        const jsonStr = trimmed.startsWith("data: ") ? trimmed.slice(6) : trimmed;
        if (!jsonStr) continue;

        let event: Record<string, any>;
        try {
          event = JSON.parse(jsonStr);
        } catch {
          continue;
        }

        if ((event.type === "progress" || event.status === "progress") && event.message) {
          // Intermediate progress event — derive step id from message text
          const stepId = stepFromMessage(event.message as string);
          if (stepId) {
            accumulated = {
              ...accumulated,
              steps_completed: [...(accumulated.steps_completed ?? []), stepId],
            };
            onStep(stepId, { ...accumulated });
          }
        } else if (event.type === "final_result" || event.is_final) {
          // Final event carries the complete results payload
          accumulated = { ...accumulated, ...event };
          onComplete(accumulated);
          return;
        } else if (event.type === "complete") {
          // Explicit completion signal after final_result
          onComplete(accumulated);
          return;
        }
      }
    }

    // Stream closed without an explicit completion event
    onComplete(accumulated);
  } catch (err: any) {
    if (err?.name !== "AbortError") {
      onError(err);
    }
  }
}

/**
 * Calls the threat core analysis API after a successful image upload.
 *
 * @param fileKey       - The file key returned by the upload API
 * @param bearerToken   - X-API-KEY header value
 * @param assumptions   - Optional list of assumption strings
 */
export async function callThreatCore(
  fileKey: string,
  bearerToken: string,
  assumptions?: string[]
): Promise<ThreatCoreResponse> {
  const requestBody: Record<string, any> = { file_key: fileKey };
  if (assumptions && assumptions.length > 0) {
    requestBody["Assumptions"] = assumptions;
  }

  console.log("[callThreatCore] POST", `${apiURL}/api/threat/core`);
  console.log("[callThreatCore] request body:", JSON.stringify(requestBody));

  const response = await fetch(`${apiURL}/api/threat/core`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": bearerToken,
    },
    body: JSON.stringify(requestBody),
  });

  const responseText = await response.text();
  console.log("[callThreatCore] status:", response.status);
  console.log("[callThreatCore] response body:", responseText);

  if (!response.ok) {
    throw new Error(`Threat core API failed (${response.status}): ${responseText}`);
  }

  try {
    return JSON.parse(responseText);
  } catch {
    throw new Error(`Threat core succeeded but response is not JSON: ${responseText}`);
  }
}

/**
 * Calls /api/report/generate-all and downloads the ZIP file returned by the backend.
 * The ZIP contains PDF, JSON, and DOCX reports.
 * Returns the filename used for the download.
 */
export async function generateAllReport(
  fileKey: string,
  bearerToken: string
): Promise<string> {
  const url = `${apiURL}/api/report/generate-all`;
  const body = {
    file_key: fileKey,
    include_image: true,
    include_summary: true,
    include_threats: true,
  };

  console.log("[generateAllReport] POST", url);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": bearerToken,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`Report generation failed (${response.status}): ${text}`);
  }

  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
  const filename = match?.[1]?.trim() || `threat-model-report-${Date.now()}.zip`;

  const arrayBuffer = await response.arrayBuffer();
  const blob = new Blob([arrayBuffer], { type: "application/zip" });
  const objectUrl = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);

  return filename;
}
